import { Link as RouterLink } from "react-router-dom";
import { Check } from "@mui/icons-material";
import {
  Checkbox,
  Link,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import StyledTable from "components/StyledTable";
import { TimeEntry } from "models/TimeEntry";
import AbsenceTypeChip from "components/AbsenceTypeChip";
import { useTimeEntriesContext } from "../TimeEntriesContext";
import { useAuthContext } from "features/auth/AuthContext";
import AbsenceStateIcon from "components/AbsenceStateIcon";

type TimeEntryTableProps = {
  sortBy: keyof TimeEntry;
  setSortBy: (column: keyof TimeEntry) => void;
  sortDir: "asc" | "desc";
  setSortDir: (dir: "asc" | "desc") => void;
};

const TimeEntryTable: React.FC<TimeEntryTableProps> = ({
  sortBy,
  setSortBy,
  sortDir,
  setSortDir,
}) => {
  const { user } = useAuthContext();
  const { entries, selectedEntries, toggleSelection, setSelectedEntries } = useTimeEntriesContext();

  const isAdminOrPayroll = user?.role === "ADMIN" || user?.role === "PAYROLL";

  function renderSortLabel(column: keyof TimeEntry, label: string) {
    return (
      <TableSortLabel
        active={sortBy === column}
        direction={sortBy === column ? sortDir : "asc"}
        onClick={() =>
          sortBy === column ? setSortDir(sortDir === "asc" ? "desc" : "asc") : setSortBy(column)
        }
      >
        {label}
      </TableSortLabel>
    );
  }

  return (
    <TableContainer>
      <StyledTable size="small" striped>
        <TableHead>
          <TableRow>
            {isAdminOrPayroll && (
              <TableCell padding="checkbox">
                <Checkbox
                  disabled={!entries || entries.length === 0}
                  indeterminate={
                    selectedEntries.length > 0 && selectedEntries.length < (entries?.length || 0)
                  }
                  checked={selectedEntries.length === entries?.length && entries?.length > 0}
                  onChange={() => {
                    if (selectedEntries.length > 0) {
                      setSelectedEntries([]);
                    } else {
                      setSelectedEntries(entries.map((entry) => entry.id));
                    }
                  }}
                />
              </TableCell>
            )}
            <TableCell>{renderSortLabel("userName", "Medarbetare")}</TableCell>
            {isAdminOrPayroll && (
              <>
                <TableCell>{renderSortLabel("userHogiaNumber", "Anst.nr")}</TableCell>
                <TableCell>{renderSortLabel("createdAt", "Skapad")}</TableCell>
                <TableCell>{renderSortLabel("type", "Typ")}</TableCell>
              </>
            )}
            <TableCell>{renderSortLabel("from", "Från")}</TableCell>
            <TableCell>{renderSortLabel("to", "Till")}</TableCell>
            {isAdminOrPayroll && (
              <>
                <TableCell align="right">{renderSortLabel("state", "Status")}</TableCell>
                <TableCell align="right">{renderSortLabel("processed", "Behandlad")}</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>

        <TableBody>
          {!entries?.length && (
            <TableRow>
              <TableCell colSpan={100} align="center" sx={{ fontStyle: "italic" }}>
                Ingen data matchar sökningen
              </TableCell>
            </TableRow>
          )}

          {entries.map((entry) => (
            <TableRow key={entry.id}>
              {isAdminOrPayroll && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedEntries.includes(entry.id)}
                    onChange={() => toggleSelection(entry.id)}
                  />
                </TableCell>
              )}
              <TableCell>
                <Link component={RouterLink} to={`/users/view/${entry.userId}`}>
                  {entry.userName}
                </Link>
              </TableCell>
              {isAdminOrPayroll && (
                <>
                  <TableCell sx={{ fontSize: "small" }}>{entry.userHogiaNumber}</TableCell>
                  <TableCell sx={{ fontSize: "small" }}>{entry.createdAt}</TableCell>
                  <TableCell>
                    <AbsenceTypeChip entry={entry} />
                  </TableCell>
                </>
              )}
              <TableCell sx={{ fontSize: "small" }}>{entry.from}</TableCell>
              <TableCell sx={{ fontSize: "small" }}>{entry.to}</TableCell>
              {isAdminOrPayroll && (
                <>
                  <TableCell align="right" sx={{ width: 0 }}>
                    <AbsenceStateIcon entry={entry} />
                  </TableCell>
                  <TableCell align="right" sx={{ width: 0 }}>
                    {entry.processed && <Check />}
                  </TableCell>
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </StyledTable>
    </TableContainer>
  );
};

export default TimeEntryTable;
